/**
 * SCWT-PM-V2 Phase 5 — Landlord dashboard (minimal Phase 5 slice).
 * Route: /landlord
 *
 * Confirms links → shows portfolio + basic consent panel.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Home, LogOut, ShieldCheck, CheckCircle2, AlertCircle } from "lucide-react";
import axios from "axios";
import { BrandLogo } from "@/components/BrandLogo";

const base = process.env.REACT_APP_BACKEND_URL;
const client = axios.create({ baseURL: `${base}/api`, withCredentials: true });

export default function LandlordDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [meRes, portRes] = await Promise.all([
          client.get("/landlord-portal/me"),
          client.get("/landlord-portal/properties"),
        ]);
        setMe(meRes.data);
        setPortfolio(portRes.data);
      } catch (e) {
        if (e?.response?.status === 401) navigate("/landlord/login");
      }
    })();
  }, [navigate]);

  const confirm = async (link, type) => {
    setBusy(true);
    try {
      await client.post(`/landlord-portal/property-links/${link.link_id}/confirm`, {
        relationship_type: type, name: me?.display_name || "",
        phone: me?.phone || "", tos_accepted_version: "landlord_terms_v1_2026_02",
      });
      const portRes = await client.get("/landlord-portal/properties");
      setPortfolio(portRes.data);
    } catch (e) { alert(e?.response?.data?.detail || "Could not confirm"); }
    finally { setBusy(false); }
  };

  const dispute = async (link) => {
    const reason = window.prompt("Please tell us why this doesn't look right:", "not_my_property");
    if (!reason) return;
    setBusy(true);
    try {
      await client.post(`/landlord-portal/property-links/${link.link_id}/dispute`, { reason });
      const portRes = await client.get("/landlord-portal/properties");
      setPortfolio(portRes.data);
    } finally { setBusy(false); }
  };

  const logout = async () => {
    await client.post("/landlord-portal/logout");
    navigate("/landlord/login");
  };

  if (!me) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-700" /></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <BrandLogo to="/" size="sm" />
          <div className="flex items-center gap-3">
            <span data-testid="landlord-me-email" className="text-xs text-slate-500">{me.email}</span>
            <button data-testid="landlord-logout" onClick={logout}
                    className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
              <LogOut className="w-3 h-3" /> Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-cyan-700">
          <ShieldCheck className="w-3.5 h-3.5" /> AWARE · Landlord portal
        </div>
        <h1 className="text-2xl font-bold text-slate-900">Hi {me.display_name || me.email.split("@")[0]}</h1>

        <section data-testid="landlord-properties" className="space-y-4">
          {(portfolio?.links || []).length === 0 && (
            <div data-testid="landlord-portfolio-empty" className="bg-white rounded-2xl border border-slate-200 p-8 text-center text-sm text-slate-500">
              No properties yet. Once a property manager invites you, they'll appear here.
            </div>
          )}
          {(portfolio?.links || []).map((link) => (
            <div key={link.link_id} data-testid={`landlord-link-${link.link_id}`} className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <Home className="w-3.5 h-3.5" /> {link.agency?.name}
                  </div>
                  <div className="mt-1 text-lg font-bold text-slate-900">
                    {link.property?.address?.street}, {link.property?.address?.suburb} {link.property?.address?.state} {link.property?.address?.postcode}
                  </div>
                </div>
                <StatusBadge status={link.link_status} />
              </div>
              {link.link_status === "pending_confirmation" || link.link_status === "invited" ? (
                <div className="mt-5 border-t border-slate-100 pt-4">
                  <p className="text-sm text-slate-600 mb-3">Please confirm your relationship to this property before we open the reports.</p>
                  <div className="flex flex-wrap gap-2">
                    <ConfirmBtn onClick={() => confirm(link, "owner")} testid={`confirm-owner-${link.link_id}`}>I'm the owner</ConfirmBtn>
                    <ConfirmBtn onClick={() => confirm(link, "co_owner")} testid={`confirm-co-owner-${link.link_id}`}>I'm a co-owner</ConfirmBtn>
                    <ConfirmBtn onClick={() => confirm(link, "authorised_representative")} testid={`confirm-rep-${link.link_id}`}>I'm authorised to act</ConfirmBtn>
                    <button onClick={() => dispute(link)}
                            data-testid={`dispute-${link.link_id}`}
                            className="text-xs border border-rose-200 text-rose-700 rounded-full px-3 py-1.5 hover:bg-rose-50">
                      Not my property
                    </button>
                  </div>
                </div>
              ) : link.link_status === "active" ? (
                <div className="mt-4 text-xs text-slate-500">Reports and retest schedule will appear here as they are published.</div>
              ) : link.link_status === "disputed" ? (
                <div data-testid="landlord-link-disputed" className="mt-4 text-sm text-rose-700 inline-flex items-center gap-1"><AlertCircle className="w-4 h-4" /> Under admin review.</div>
              ) : null}
            </div>
          ))}
        </section>
      </main>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    active:                { label: "Active",              cls: "bg-emerald-100 text-emerald-800" },
    pending_confirmation:  { label: "Awaiting confirmation", cls: "bg-amber-100 text-amber-800" },
    invited:               { label: "Invited",             cls: "bg-cyan-100 text-cyan-800" },
    disputed:              { label: "Under review",        cls: "bg-rose-100 text-rose-800" },
    revoked:               { label: "Revoked",             cls: "bg-slate-100 text-slate-600" },
    ended:                 { label: "Ended",               cls: "bg-slate-100 text-slate-600" },
    cancelled:             { label: "Cancelled",           cls: "bg-slate-100 text-slate-600" },
  }[status] || { label: status, cls: "bg-slate-100 text-slate-600" };
  return <span data-testid={`landlord-link-status-${status}`} className={`inline-flex items-center rounded-full text-[11px] font-semibold px-2.5 py-1 uppercase tracking-wide ${map.cls}`}>{map.label}</span>;
}

function ConfirmBtn({ onClick, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
            className="text-xs border border-cyan-200 text-cyan-800 rounded-full px-3 py-1.5 hover:bg-cyan-50 inline-flex items-center gap-1">
      <CheckCircle2 className="w-3 h-3" /> {children}
    </button>
  );
}
